import toast from "@/components/ui/toast";
import {
  type ShareVoiceSidebarFilter,
  ShareVoiceSidebarFilterQuery,
  type ShareVoiceSidebarFilterVariables,
} from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import { client } from "@/lib/urqlProvider";
import useShareOfVoiceSidebarFilters from "./shareOfVoice-filters.slice";

/**
 * Fetches all sidebar filters
 */
export const fetchSideBarFilters = async (
  variables?: ShareVoiceSidebarFilterVariables,
): Promise<boolean> => {
  useShareOfVoiceSidebarFilters.setState(
    { isFetchingSidebarFilters: true },
    false,
    "[User] Sidebar Filters: Fetching sidebar filters",
  );

  try {
    const result = await client
      .query(ShareVoiceSidebarFilterQuery, variables || {})
      .toPromise();

    if (result.error) {
      console.error("Error fetching sidebar filters:", result.error);
      toast.error("Failed to fetch sidebar filters.");
      useShareOfVoiceSidebarFilters.setState(
        { isFetchingSidebarFilters: false },
        false,
        "[User] Sidebar Filters: Fetch sidebar filters error",
      );
      return false;
    }

    // Set isLoadingCountAndPercentage to true for all filters on query fetch
    const sidebarFilters = (result.data?.shareVoiceSidebar || []).map(
      (filter) => ({
        ...filter,
        isLoadingCountAndPercentage: true,
      }),
    );

    useShareOfVoiceSidebarFilters.setState(
      {
        isFetchingSidebarFilters: false,
        sidebarFilters,
      },
      false,
      "[User] Sidebar Filters: Fetch sidebar filters success",
    );

    return true;
  } catch (error) {
    console.error("Error fetching sidebar filters:", error);
    toast.error("An error occurred while fetching sidebar filters.");
    useShareOfVoiceSidebarFilters.setState(
      { isFetchingSidebarFilters: false },
      false,
      "[User] Sidebar Filters: Fetch sidebar filters error",
    );
    return false;
  }
};

/**
 * Updates multiple filters from subscription data in a single batch
 */
export const updateFiltersViaSubscription = (
  updatedFilters: ShareVoiceSidebarFilter,
): void => {
  const currentState = useShareOfVoiceSidebarFilters.getState();
  const { sidebarFilters } = currentState;

  // Create a Map of current filters for efficient lookups
  const currentFiltersMap = new Map(
    sidebarFilters.map((filter) => [
      `${filter.categoryLabel}-${filter.segmentId}`,
      filter,
    ]),
  );

  // Create a Map of updated filters
  const updatedFiltersMap = new Map(
    updatedFilters.map((filter) => [
      `${filter.categoryLabel}-${filter.segmentId}`,
      filter,
    ]),
  );

  // Update existing filters and add new ones
  const resultFiltersMap = new Map(currentFiltersMap);

  // Update existing filters and add new ones
  for (const [key, updatedFilter] of updatedFiltersMap) {
    const existingFilter = currentFiltersMap.get(key);

    if (existingFilter) {
      // Update existing filter
      resultFiltersMap.set(key, {
        ...existingFilter,
        ...updatedFilter,
        isLoadingCountAndPercentage: false,
      });
    } else {
      // Add new filter
      resultFiltersMap.set(key, {
        ...updatedFilter,
        isLoadingCountAndPercentage: false,
      });
    }
  }

  // Remove filters for deleted segments in categories included in the update
  const categoriesInUpdate = new Set(
    updatedFilters.map((filter) => filter.categoryLabel),
  );
  const updatedSegmentIds = new Set(
    updatedFilters.map((filter) => filter.segmentId),
  );

  // Remove filters that are in updated categories but not in the subscription
  for (const [key, filter] of resultFiltersMap) {
    if (categoriesInUpdate.has(filter.categoryLabel)) {
      if (!updatedSegmentIds.has(filter.segmentId)) {
        resultFiltersMap.delete(key);
      }
    }
  }

  // Convert back to array and sort
  const finalFilters = Array.from(resultFiltersMap.values()).sort(
    (a, b) => b.total - a.total,
  );

  useShareOfVoiceSidebarFilters.setState(
    { sidebarFilters: finalFilters },
    false,
    "[Subscription] Sidebar Filters: Batch updated via subscription",
  );
};
